// AlignUI Checkbox v0.0.0

import * as React from "react";
import * as CheckboxPrimitive from "@radix-ui/react-checkbox";
import { cn } from "../utils/cn";

// Fixed: Simple React.ComponentProps<"svg"> - no SVGSVGElement needed
function IconCheck(props: React.ComponentProps<"svg">) {
  return (
    <svg
      width="10"
      height="8"
      viewBox="0 0 10 8"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      {...props}
    >
      <path d="M1 3.5L4 6.5L9 1.5" strokeWidth="1.5" className="stroke-static-white" />
    </svg>
  );
}

function IconIndeterminate(props: React.ComponentProps<"svg">) {
  return (
    <svg
      width="8"
      height="2"
      viewBox="0 0 8 2"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      {...props}
    >
      <path d="M0 1H8" strokeWidth="1.5" className="stroke-static-white" />
    </svg>
  );
}

// Rest of your code remains EXACTLY same...
export const Checkbox = React.forwardRef<
  React.ElementRef<typeof CheckboxPrimitive.Root>,
  React.ComponentPropsWithoutRef<typeof CheckboxPrimitive.Root>
>(({ className, checked, ...props }, ref) => {
  const filterId = React.useId();

  const TOTAL_LENGTH_CHECK = 11.313708305358887;
  const TOTAL_LENGTH_INDETERMINATE = 8;

  return (
    <CheckboxPrimitive.Root
      ref={ref}
      checked={checked}
      className={cn(
        "group/checkbox relative flex size-5 shrink-0 items-center justify-center outline-none cursor-pointer disabled:cursor-not-allowed",
        className
      )}
      {...props}
    >
      <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
        <rect
          x="2"
          y="2"
          width="16"
          height="16"
          rx="4"
          className={cn(
            "fill-bg-soft-200 transition",
            "group-hover/checkbox:fill-bg-sub-300",
            "group-data-[state=checked]/checkbox:fill-primary-base",
            "group-data-[state=indeterminate]/checkbox:fill-primary-base"
          )}
        />

        <g filter={`url(#${filterId})`}>
          <rect
            x="3.5"
            y="3.5"
            width="13"
            height="13"
            rx="2.6"
            className={cn(
              "fill-bg-white-0 transition",
              "group-data-[state=checked]/checkbox:opacity-0",
              "group-data-[state=indeterminate]/checkbox:opacity-0"
            )}
          />
        </g>

        <defs>
          <filter id={filterId} x="1.5" y="3.5" width="17" height="17" filterUnits="userSpaceOnUse">
            <feOffset dy="2" />
            <feGaussianBlur stdDeviation="1" />
            <feColorMatrix values="0 0 0 0 0.1 0 0 0 0 0.1 0 0 0 0 0.1 0 0 0 0.12 0" />
          </filter>
        </defs>
      </svg>

      {/* Indicator */}
      <CheckboxPrimitive.Indicator forceMount>
        <IconCheck
          className={cn(
            "absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2",
            "opacity-0 group-data-[state=checked]/checkbox:opacity-100",
            "[&>path]:[stroke-dasharray:var(--len)] [&>path]:[stroke-dashoffset:var(--len)]",
            "group-data-[state=checked]/checkbox:[&>path]:[stroke-dashoffset:0]"
          )}
          style={{ "--len": `${TOTAL_LENGTH_CHECK}` } as React.CSSProperties}
        />

        <IconIndeterminate
          className={cn(
            "absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2",
            "opacity-0 group-data-[state=indeterminate]/checkbox:opacity-100",
            "[&>path]:[stroke-dasharray:var(--len)] [&>path]:[stroke-dashoffset:var(--len)]",
            "group-data-[state=indeterminate]/checkbox:[&>path]:[stroke-dashoffset:0]"
          )}
          style={{ "--len": `${TOTAL_LENGTH_INDETERMINATE}` } as React.CSSProperties}
        />
      </CheckboxPrimitive.Indicator>
    </CheckboxPrimitive.Root>
  );
});

Checkbox.displayName = "Checkbox";
